import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const testimonials = [
  {
    id: 1,
    name: { en: 'Ahmed Al-Rashid', ar: 'أحمد الراشد' },
    company: { en: 'Marina Properties LLC', ar: 'مارينا العقارية ذ.م.م' },
    role: { en: 'Property Developer', ar: 'مطور عقاري' },
    quote: {
      en: 'Glow Space transformed our high-rise building with stunning aluminium facades. Their attention to detail and professional approach made the entire process seamless. Highly recommended for any commercial project.',
      ar: 'حولت جلو سبيس مبنانا الشاهق بواجهات ألمنيوم مذهلة. اهتمامهم بالتفاصيل ونهجهم المحترف جعل العملية بأكملها سلسة. موصى به بشدة لأي مشروع تجاري.',
    },
    rating: 5,
    image: null,
  },
  {
    id: 2,
    name: { en: 'Sarah Thompson', ar: 'سارة طومسون' },
    company: { en: 'Dubai Design Studio', ar: 'استوديو دبي للتصميم' },
    role: { en: 'Interior Designer', ar: 'مصممة داخلية' },
    quote: {
      en: 'I\'ve worked with many contractors, but Glow Space stands out for their quality and reliability. The frameless glass partitions they installed for our office are absolutely beautiful and exactly what we envisioned.',
      ar: 'عملت مع العديد من المقاولين، لكن جلو سبيس تتميز بجودتها وموثوقيتها. الفواصل الزجاجية بدون إطار التي ركبوها لمكتبنا جميلة للغاية وتماماً كما تصورنا.',
    },
    rating: 5,
    image: null,
  },
  {
    id: 3,
    name: { en: 'Mohammed Al-Qasimi', ar: 'محمد القاسمي' },
    company: { en: 'Villa Owner - Jumeirah', ar: 'مالك فيلا - جميرا' },
    role: { en: 'Homeowner', ar: 'صاحب منزل' },
    quote: {
      en: 'From the initial consultation to the final installation, the team was exceptional. They replaced all windows in my villa with high-quality aluminium frames. Great value for money and excellent after-service support.',
      ar: 'من الاستشارة الأولية إلى التركيب النهائي، كان الفريق استثنائياً. قاموا باستبدال جميع النوافذ في فيلتي بإطارات ألمنيوم عالية الجودة. قيمة ممتازة مقابل المال ودعم خدمة ما بعد البيع ممتاز.',
    },
    rating: 5,
    image: null,
  },
  {
    id: 4,
    name: { en: 'Fatima Hassan', ar: 'فاطمة حسن' },
    company: { en: 'Retail Solutions ME', ar: 'حلول التجزئة الشرق الأوسط' },
    role: { en: 'Store Manager', ar: 'مديرة متجر' },
    quote: {
      en: 'Our new shopfront glass installation has significantly increased foot traffic. The team completed the work on time and within budget. Their professionalism is unmatched in the industry.',
      ar: 'تركيب زجاج واجهة متجرنا الجديد زاد بشكل كبير من حركة المرور. أنجز الفريق العمل في الوقت المحدد وضمن الميزانية. احترافيتهم لا مثيل لها في الصناعة.',
    },
    rating: 5,
    image: null,
  },
  {
    id: 5,
    name: { en: 'James Wilson', ar: 'جيمس ويلسون' },
    company: { en: 'Emirates Construction', ar: 'الإمارات للإنشاءات' },
    role: { en: 'Project Manager', ar: 'مدير مشروع' },
    quote: {
      en: 'We\'ve partnered with Glow Space on multiple projects. Their Canadian standards combined with local expertise make them our go-to contractor for all aluminium and glass requirements.',
      ar: 'تعاونا مع جلو سبيس في مشاريع متعددة. معاييرهم الكندية مع الخبرة المحلية تجعلهم المقاول المفضل لدينا لجميع متطلبات الألمنيوم والزجاج.',
    },
    rating: 5,
    image: null,
  },
];

const Testimonials = () => {
  const { t, language, isRTL } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section 
      className="py-20 relative"
      id="testimonials"
      data-testid="testimonials-section"
    >
      {/* Glassy background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-cyan-50/50 to-white/40"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
          >
            {t('testimonials.title')}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Testimonial Card */}
        <div className="relative max-w-4xl mx-auto">
          {/* Quote Icon */}
          <div className="absolute -top-6 left-8 md:left-12 z-10">
            <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
              <Quote size={24} className="text-white" />
            </div>
          </div>

          {/* Main Card */}
          <div 
            className="glass-card rounded-3xl p-8 md:p-12 relative overflow-hidden"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#25D366]/20 to-emerald-300/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-sky-300/20 to-cyan-300/10 rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10">
              {/* Stars */}
              <div className={`flex gap-1 mb-6 ${isRTL ? 'justify-end' : ''}`}>
                {[...Array(currentTestimonial.rating)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={20} 
                    className="text-yellow-400 fill-yellow-400" 
                  />
                ))}
              </div>

              {/* Quote */}
              <blockquote 
                className={`text-lg md:text-xl text-slate-700 leading-relaxed mb-8 ${isRTL ? 'text-right' : ''}`}
                style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif" }}
              >
                "{currentTestimonial.quote[language]}"
              </blockquote>

              {/* Author */}
              <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#25D366] to-emerald-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                  {currentTestimonial.name[language].charAt(0)}
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                  <p 
                    className="text-slate-800 font-semibold text-lg"
                    style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
                  >
                    {currentTestimonial.name[language]}
                  </p>
                  <p className="text-slate-500 text-sm">
                    {currentTestimonial.role[language]} • {currentTestimonial.company[language]}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={isRTL ? goToNext : goToPrev}
            className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? '-right-4 md:-right-16' : '-left-4 md:-left-16'} w-10 h-10 md:w-12 md:h-12 glass-card rounded-full flex items-center justify-center text-slate-600 hover:text-slate-800 transition-colors`}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev-btn"
          >
            {isRTL ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
          </button>
          <button
            onClick={isRTL ? goToPrev : goToNext}
            className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? '-left-4 md:-left-16' : '-right-4 md:-right-16'} w-10 h-10 md:w-12 md:h-12 glass-card rounded-full flex items-center justify-center text-slate-600 hover:text-slate-800 transition-colors`}
            aria-label="Next testimonial"
            data-testid="testimonial-next-btn"
          >
            {isRTL ? <ChevronLeft size={24} /> : <ChevronRight size={24} />}
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-[#25D366] w-8' 
                  : 'bg-slate-300 hover:bg-slate-400'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
              data-testid={`testimonial-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
